import { useEffect, useState } from "react";
import { supabase } from "../lib/SupabaseClient";
import QuestionEditorPro, { Question } from "../components/QuestionEditorPro";

/* ------------------ Debounce Hook ------------------ */
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debouncedValue;
}

export default function QuestionsPage() {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [infoMessage, setInfoMessage] = useState<string>("");

    // 🔍 Filters
    const [filterBankId, setFilterBankId] = useState<number | null>(null);
    const [filterType, setFilterType] = useState<string>("");
    const [filterText, setFilterText] = useState<string>("");
    const debouncedText = useDebounce(filterText, 300);

    /* ------------------ Load Question Banks ------------------ */
    useEffect(() => {
        async function loadBanks() {
            const { data, error } = await supabase
                .from("question_banks")
                .select("id, name")
                .order("name");
            if (!error && data) setBanks(data);
        }
        loadBanks();
    }, []);

    /* ------------------ Load Questions ------------------ */
    const loadQuestions = async () => {
        try {
            setLoading(true);
            setError(null);

            const { data, error } = await supabase
                .from("questions")
                .select("*, choices(*)")
                .order("id", { ascending: true });

            if (error) throw error;

            let filtered = data || [];

            // Apply filters
            if (filterBankId) filtered = filtered.filter((q) => q.bank_id === filterBankId);
            if (filterType) filtered = filtered.filter((q) => q.type === filterType);

            // Text filter (matches question text & choices)
            if (debouncedText) {
                const search = debouncedText.toLowerCase();
                filtered = filtered.filter(
                    (q) =>
                        q.text.toLowerCase().includes(search) ||
                        q.choices?.some((c: any) => c.text.toLowerCase().includes(search))
                );
            }

            setQuestions(filtered);
        } catch (err: any) {
            console.error("❌ Error loading questions:", err.message);
            setError("Failed to load questions. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadQuestions();
    }, [filterBankId, filterType, debouncedText]);

    /* ------------------ Handlers ------------------ */
    const handleEditClick = async (questionId: number) => {
        try {
            setInfoMessage("Loading question details...");
            const { data, error } = await supabase
                .from("questions")
                .select("*, choices(*)")
                .eq("id", questionId)
                .single();

            if (error) throw error;

            setEditingQuestion(data);
            setInfoMessage("");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err: any) {
            console.error("❌ Error fetching question:", err.message);
            setInfoMessage("❌ Could not load question details.");
        }
    };

    const handleCancelEdit = () => {
        setEditingQuestion(null);
        setInfoMessage("");
    };

    const handleSaved = () => {
        handleCancelEdit();
        setInfoMessage("✅ Question saved successfully!");
        loadQuestions();
    };

    const handleResetFilters = () => {
        setFilterBankId(null);
        setFilterType("");
        setFilterText("");
    };

    /* ------------------ UI States ------------------ */
    if (loading) return <p className="p-6 text-gray-700">Loading questions...</p>;
    if (error) return <p className="p-6 text-red-600">{error}</p>;

    /* ------------------ Render ------------------ */
    return (
        <div className="bg-white p-4 rounded shadow">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-indigo-700">Questions</h2>

                {!editingQuestion ? (
                    <button
                        onClick={() => setEditingQuestion({} as Question)}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm transition"
                    >
                        ➕ Add New Question
                    </button>
                ) : (
                    <button
                        onClick={handleCancelEdit}
                        className="text-sm text-gray-500 hover:underline"
                    >
                        Cancel Edit
                    </button>
                )}
            </div>

            {/* Info Message */}
            {infoMessage && (
                <div className="mb-4 text-sm bg-yellow-50 border border-yellow-300 text-yellow-800 p-2 rounded">
                    {infoMessage}
                </div>
            )}

            {/* Conditional rendering */}
            {editingQuestion ? (
                <div className="animate-fade-in">
                    <QuestionEditorPro
                        key={editingQuestion?.id || "new"}
                        testId={1}
                        question={editingQuestion ?? undefined}
                        onSaved={handleSaved}
                    />
                </div>
            ) : (
                <>
                    {/* 🔍 Filters */}
                    <div className="bg-indigo-50 border border-indigo-100 rounded-md p-3 mb-4 flex flex-wrap gap-3 items-end">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Bank</label>
                            <select
                                value={filterBankId ?? ""}
                                onChange={(e) =>
                                    setFilterBankId(e.target.value ? Number(e.target.value) : null)
                                }
                                className="border rounded-md p-2 text-sm"
                            >
                                <option value="">All Banks</option>
                                {banks.map((b) => (
                                    <option key={b.id} value={b.id}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="border rounded-md p-2 text-sm"
                            >
                                <option value="">All Types</option>
                                <option value="multiple_choice">Multiple Choice</option>
                                <option value="true_false">True / False</option>
                                <option value="short_answer">Short Answer</option>
                                <option value="essay">Essay</option>
                            </select>
                        </div>

                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Search
                            </label>
                            <input
                                type="text"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                placeholder="Search text or choice..."
                                className="border rounded-md p-2 w-full text-sm"
                            />
                        </div>

                        <button
                            onClick={handleResetFilters}
                            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm transition"
                        >
                            Reset
                        </button>
                    </div>

                    {/* Questions Table */}
                    <div className="overflow-x-auto mt-4 animate-fade-in">
                        <table className="w-full border text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-left">
                                    <th className="p-2 border">ID</th>
                                    <th className="p-2 border">Text</th>
                                    <th className="p-2 border">Type</th>
                                    <th className="p-2 border">Choices</th>
                                    <th className="p-2 border">Image</th>
                                    <th className="p-2 border">Created</th>
                                    <th className="p-2 border text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {questions.map((q) => (
                                    <tr key={q.id} className="hover:bg-gray-50">
                                        <td className="p-2 border text-gray-700">{q.id}</td>
                                        <td
                                            className="p-2 border text-gray-800 max-w-[350px] truncate hover:whitespace-normal hover:bg-indigo-50 cursor-help transition"
                                            title={q.text.replace(/<[^>]+>/g, "")}
                                        >
                                            <div
                                                className="line-clamp-2 overflow-hidden text-ellipsis"
                                                dangerouslySetInnerHTML={{ __html: q.text }}
                                            />
                                        </td>
                                        <td className="p-2 border text-gray-600">{q.type}</td>
                                        <td className="p-2 border text-gray-700 max-w-[250px] truncate">
                                            <div className="flex flex-wrap gap-1">
                                                {q.choices?.map((c: any) => (
                                                    <span
                                                        key={c.id}
                                                        className={`px-2 py-0.5 rounded-full text-xs ${c.is_correct
                                                                ? "bg-green-50 text-green-700 border border-green-200"
                                                                : "bg-red-50 text-red-700 border border-red-200"
                                                            }`}
                                                    >
                                                        {c.is_correct ? "✔" : "✖"} {c.text}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="p-2 border text-center">
                                            {q.image_url ? (
                                                <img
                                                    src={q.image_url}
                                                    alt="Helper"
                                                    className="h-10 w-10 object-cover rounded border"
                                                />
                                            ) : (
                                                <span className="text-gray-400 text-xs">(none)</span>
                                            )}
                                        </td>
                                        <td className="p-2 border text-gray-600">
                                            {q.created_utc
                                                ? new Date(q.created_utc).toLocaleDateString()
                                                : "-"}
                                        </td>
                                        <td className="p-2 border text-center">
                                            <button
                                                onClick={() => handleEditClick(q.id!)}
                                                className="text-blue-600 hover:underline"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {questions.length === 0 && (
                            <p className="text-center text-gray-500 py-6">No questions found.</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
